// client/src/components/inbox/InboxTriagePanel.jsx
//
// Community-management triage for one inbox conversation (On-Screen 2.0):
// status pipeline, assignee, priority, internal notes. Dark-panel styling to
// match SocialInbox.jsx.

import React, { useCallback, useEffect, useState } from 'react';
import {
  setInboxStatus, assignInboxItem, fetchInboxNotes, addInboxNote,
  INBOX_WORKFLOW_STATUSES, INBOX_WORKFLOW_LABELS,
} from '../../services/inboxApi';
import { listTenantUsers } from '../../services/tenantUsersApi';

const STATUS_COLOR = {
  new: '#8892b0',
  assigned: '#ffa500',
  in_progress: '#4aa3ff',
  responded: '#7cc576',
  resolved: '#4CAF50',
};

export default function InboxTriagePanel({ tenantId, item, onUpdated }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');

  const status = item.workflow_status || 'new';

  useEffect(() => {
    let alive = true;
    listTenantUsers(tenantId)
      .then((r) => { if (alive) setUsers(r.users || r.data || r || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tenantId]);

  const loadNotes = useCallback(async () => {
    try {
      const r = await fetchInboxNotes(tenantId, item.id);
      setNotes(r.notes || []);
    } catch { setNotes([]); }
  }, [tenantId, item.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const changeStatus = async (to) => {
    setBusy(true); setError('');
    try {
      await setInboxStatus(tenantId, item.id, to);
      onUpdated?.({ ...item, workflow_status: to });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const changeAssignee = async (rawUserId) => {
    const userId = rawUserId ? Number(rawUserId) : null;
    setBusy(true); setError('');
    try {
      const r = await assignInboxItem(tenantId, item.id, userId);
      onUpdated?.({
        ...item,
        assigned_to: userId,
        assigned_to_username: users.find((u) => String(u.id) === String(userId))?.username || null,
        workflow_status: r.workflow_status || status,
      });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const submitNote = async () => {
    if (!noteDraft.trim()) return;
    setBusy(true); setError('');
    try {
      await addInboxNote(tenantId, item.id, noteDraft.trim());
      setNoteDraft('');
      loadNotes();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const box = {
    borderTop: '1px solid #444', paddingTop: 12, marginTop: 12,
  };
  const label = { color: '#a8a8a8', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div style={box}>
      {error && <div style={{ color: '#ff8888', fontSize: '0.75rem', marginBottom: 6 }}>{error}</div>}

      <div style={{ ...label, marginBottom: 4 }}>Triage</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
        {INBOX_WORKFLOW_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            disabled={busy || s === status}
            onClick={() => changeStatus(s)}
            style={{
              background: s === status ? STATUS_COLOR[s] : 'rgba(255,255,255,0.08)',
              color: s === status ? '#1a1a1a' : '#ccc',
              border: `1px solid ${s === status ? STATUS_COLOR[s] : 'rgba(255,255,255,0.2)'}`,
              borderRadius: 12, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 600,
              cursor: busy || s === status ? 'default' : 'pointer',
            }}
          >
            {INBOX_WORKFLOW_LABELS[s]}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={{ flex: 1, minWidth: 160 }}>
          <span style={label}>Assignee</span>
          <select
            value={item.assigned_to || ''}
            disabled={busy}
            onChange={(e) => changeAssignee(e.target.value)}
            style={{
              width: '100%', marginTop: 2, background: '#1a1a2e', color: '#fff',
              border: '1px solid #444', borderRadius: 6, padding: '5px 8px', fontSize: '0.8rem',
            }}
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.username || u.email || `User ${u.id}`}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={label}>Internal notes</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', maxHeight: 120, overflowY: 'auto' }}>
        {notes.length === 0 && <li style={{ color: '#777', fontSize: '0.75rem' }}>No notes yet.</li>}
        {notes.map((n) => (
          <li key={n.id} style={{ fontSize: '0.78rem', color: '#ddd', marginBottom: 4 }}>
            <span style={{ color: '#ffa500' }}>{n.author_username || 'Someone'}</span>{' '}
            <span style={{ color: '#888' }}>{new Date(n.createdon).toLocaleDateString()}</span>
            <div>{n.note}</div>
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Add an internal note"
          style={{
            flex: 1, background: '#1a1a2e', color: '#fff', border: '1px solid #444',
            borderRadius: 6, padding: '5px 8px', fontSize: '0.8rem',
          }}
        />
        <button
          type="button"
          onClick={submitNote}
          disabled={busy || !noteDraft.trim()}
          style={{
            background: '#253866', color: '#fff', border: '1px solid #ffa500', borderRadius: 6,
            padding: '5px 12px', fontSize: '0.8rem', cursor: 'pointer',
            opacity: busy || !noteDraft.trim() ? 0.5 : 1,
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}
